import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";

type ColorMap = Record<string, string>;
type DesignMap = Record<string, string>;

interface ThemeSettingItem {
  typename: string;
  key: string;
  value: string;
}

export const useSettingsComposable = () => {
  const colorsState: Ref<ColorMap | null> = ref(null);
  const customerDesignState: Ref<DesignMap | null> = ref(null);
  const resellerDesignState: Ref<DesignMap | null> = ref(null);
  const preservableStates: Ref<string[]> = ref(["colorsState", "customerDesignState", "resellerDesignState"]);

  const setTheme = (settings: ThemeSettingItem[]) => {
    settings.forEach(item => {
      if (item.typename.toLowerCase() === "colors") {
        if (!colorsState.value) {
          colorsState.value = {};
        }
        colorsState.value[item.key] = item.value;
      }

      if (item.typename.toLowerCase() === "customerdesign") {
        if (!customerDesignState.value) {
          customerDesignState.value = {};
        }
        customerDesignState.value[item.key] = item.value;
      }

      if (item.typename.toLowerCase() === "resellerdesign") {
        if (!resellerDesignState.value) {
          resellerDesignState.value = {};
        }
        resellerDesignState.value[item.key] = item.value;
      }
    });

    if (customerDesignState.value) {
      SessionHelper.addCustomerSystemLogo(customerDesignState.value["logo"]);
    }
  };

  const reloadSettings = async () => {
    const response = await apiSkobo.get(`/Website/Layout`);
    setTheme(Object.values(response.data));
  };

  const loadSettings = async () => {
    if (!colorsState.value) {
      reloadSettings();
    }
  };

  // Getters
  const colors = computed(() => colorsState.value || {});
  const customerDesign = computed(() => customerDesignState.value || {});
  const resellerDesign = computed(() => resellerDesignState.value || {});

  const colorSetup = computed(() => {
    if (!colorsState.value) {
      return {
        "--color-tertiary-core": "#b4191e"
      };
    }

    const css: Record<string, string> = {};
    if (colorsState.value["color-primary-core"]) {
      css["--color-primary-core"] = colorsState.value["color-primary-core"];
    }

    if (colorsState.value["color-primary-light"]) {
      css["--color-primary-light"] = colorsState.value["color-primary-light"];
    }

    if (colorsState.value["color-primary-lightest"]) {
      css["--color-primary-lightest"] = colorsState.value["color-primary-lightest"];
    }

    if (colorsState.value["color-secondary-core"]) {
      css["--color-secondary-core"] = colorsState.value["color-secondary-core"];
    }

    if (colorsState.value["color-secondary-light"]) {
      css["--color-secondary-light"] = colorsState.value["color-secondary-light"];
    }

    if (colorsState.value["color-tertiary-core"]) {
      css["--color-tertiary-core"] = colorsState.value["color-tertiary-core"];
    }

    if (colorsState.value["color-danger"]) {
      css["--color-danger"] = colorsState.value["color-danger"];
    }

    if (colorsState.value["color-success"]) {
      css["--color-success"] = colorsState.value["color-success"];
    }
    return css;
  });

  return {
    colorsState,
    customerDesignState,
    resellerDesignState,
    preservableStates,
    setTheme,
    loadSettings,
    reloadSettings,
    colors,
    customerDesign,
    resellerDesign,
    colorSetup
  };
};
